import type React from "react";
import { NavLink } from "react-router";

import type { CommunityMessageActivity } from "@/components/CommunityFeed/types";
import { buttonVariants } from "@/design-system/Button";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { twResolve } from "@/helpers/tw-resolve";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface InactivePostWarningProps {
  /** Activity fields used to preselect the label and group when creating a follow-up post. */
  message: CommunityMessageActivity;
  onClose: () => void;
}

export function InactivePostWarning({ message, onClose }: InactivePostWarningProps): React.ReactNode {
  const slug = useSlug();
  const hasPermission = usePermission();
  const { t } = useTranslation();

  const canCreatePost = hasPermission((x) => x.canCreatePosts);

  return (
    <p className="flex max-w-sm flex-col gap-space-sm">
      <span className="text-caption-emphasis">{t("component.community-post.comments.old-post-warning")}</span>
      <span>
        {canCreatePost
          ? t("component.community-post.comments.old-post-warning.description")
          : t("component.community-post.comments.old-post-warning.description.no-permission")}
      </span>
      {canCreatePost && (
        <NavLink
          to={
            hasPermission((x) => x.isAdmin)
              ? routes.communityFeed.adminCreatePost({ slug })
              : routes.communityFeed.list({ slug })
          }
          onClick={onClose}
          className={twResolve(
            buttonVariants({ variant: "ghostPrimary", type: "link" }),
            "text-caption-emphasis text-text-default-inverse underline",
          )}
          state={
            {
              residentCreatePostType: message.messageLabel,
              groupId: message.group?.id,
              isGeneral: message.group == null,
            } satisfies MessageRouteStates["list"]
          }
        >
          {t("component.community-post.comments.old-post-warning.create-new")}
        </NavLink>
      )}
      {!canCreatePost && (
        <button
          type="button"
          onClick={onClose}
          className={twResolve(
            buttonVariants({ variant: "ghostPrimary", type: "button" }),
            "text-caption-emphasis text-text-default-inverse underline",
          )}
        >
          {t("common.action.cancel")}
        </button>
      )}
    </p>
  );
}
